/**
 * Text Extraction Utilities
 * ORCA360 - Manual Transcript Upload Support
 * 
 * Extracts text from various file formats:
 * - TXT (plain text)
 * - DOCX (Word documents) - requires mammoth.js
 * - PDF (documents) - requires pdfjs-dist
 * - SRT/VTT (subtitle formats with timestamps)
 * 
 * MAD-17: Sprint 4, Week 1
 */

/**
 * Parsed subtitle entry (SRT/VTT)
 */
export interface SubtitleEntry {
  index: number;
  startTime: number; // seconds
  endTime: number; // seconds
  text: string;
}

/**
 * Main text extraction function
 * Detects file type and delegates to appropriate parser
 * 
 * Currently supported formats: TXT, SRT, VTT
 * Future support (requires additional libraries): DOCX, PDF
 */
export async function extractTextFromFile(file: File): Promise<string> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'txt':
      return await extractFromTxt(file);
    case 'docx':
      return await extractFromDocx(file);  // Currently disabled
    case 'pdf':
      return await extractFromPdf(file);   // Currently disabled
    case 'srt':
      return await extractFromSrt(file);
    case 'vtt':
      return await extractFromVtt(file);
    default:
      throw new Error(`Unsupported file type: ${extension}. Currently supported: TXT, SRT, VTT`);
  }
}

/**
 * Extract text from plain text file
 */
async function extractFromTxt(file: File): Promise<string> {
  const text = await file.text();
  return text.trim();
}

/**
 * Extract text from DOCX file
 * Requires: mammoth.js library
 * 
 * Install: npm install mammoth
 * 
 * NOTE: This feature is currently disabled in production builds.
 * DOCX files are not supported. Please convert to TXT format.
 */
async function extractFromDocx(_file: File): Promise<string> {
  // DOCX extraction is disabled because mammoth is not installed
  // To enable: npm install mammoth and uncomment the code below
  throw new Error(
    'DOCX file format is not currently supported. Please convert your file to TXT format and upload again. You can use Microsoft Word: File > Save As > Plain Text (.txt)'
  );
  
  /* Uncomment this code after installing mammoth:
  try {
    const mammoth = await import('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    if (result.messages && result.messages.length > 0) {
      console.warn('[DOCX] Conversion warnings:', result.messages);
    }
    
    return result.value.trim();
  } catch (error) {
    console.error('[DOCX] Extraction error:', error);
    throw new Error(
      'Failed to extract text from DOCX file. Please use TXT format instead.'
    );
  }
  */
}

/**
 * Extract text from PDF file
 * Requires: pdfjs-dist library
 * 
 * Install: npm install pdfjs-dist
 * 
 * NOTE: This feature is currently disabled in production builds.
 * PDF files are not supported. Please convert to TXT format.
 */
async function extractFromPdf(_file: File): Promise<string> {
  // PDF extraction is disabled because pdfjs-dist is not installed
  // To enable: npm install pdfjs-dist and uncomment the code below
  throw new Error(
    'PDF file format is not currently supported. Please convert your file to TXT format and upload again. You can open the PDF, select all text (Ctrl+A), copy (Ctrl+C), and paste into a text editor.'
  );
  
  /* Uncomment this code after installing pdfjs-dist:
  try {
    const pdfjsLib = await import('pdfjs-dist');
    
    // Set worker path (required for PDF.js)
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      fullText += pageText + '\n\n';
    }
    
    return fullText.trim();
  } catch (error) {
    console.error('[PDF] Extraction error:', error);
    throw new Error(
      'Failed to extract text from PDF file. Please use TXT format instead.'
    );
  }
  */
}

/**
 * Extract text from SRT (SubRip) subtitle file
 * Format:
 * 1
 * 00:00:05,000 --> 00:00:10,000
 * [Speaker 1] This is the first subtitle.
 */
async function extractFromSrt(file: File): Promise<string> {
  const content = await file.text();
  const entries = parseSrtFile(content);
  
  // Convert to transcript format with timestamps
  return entries
    .map(entry => {
      const timestamp = formatTimestamp(entry.startTime);
      return `[${timestamp}] ${entry.text}`;
    })
    .join('\n');
}

/**
 * Parse SRT file content into subtitle entries
 */
function parseSrtFile(content: string): SubtitleEntry[] {
  const entries: SubtitleEntry[] = [];
  const blocks = content.trim().split(/\n\s*\n/); // Split by double newline
  
  for (const block of blocks) {
    const lines = block.split('\n');
    
    if (lines.length < 3) continue; // Skip invalid blocks
    
    const index = parseInt(lines[0] || '1', 10);
    const timeLine = lines[1];
    const text = lines.slice(2).join(' ');
    
    if (!timeLine) continue; // Skip if no time line
    
    // Parse time: 00:00:05,000 --> 00:00:10,000
    const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
    
    if (timeMatch && timeMatch.length >= 9) {
      const startTime = parseTimeToSeconds(
        parseInt(timeMatch[1] || '0', 10),
        parseInt(timeMatch[2] || '0', 10),
        parseInt(timeMatch[3] || '0', 10),
        parseInt(timeMatch[4] || '0', 10)
      );
      
      const endTime = parseTimeToSeconds(
        parseInt(timeMatch[5] || '0', 10),
        parseInt(timeMatch[6] || '0', 10),
        parseInt(timeMatch[7] || '0', 10),
        parseInt(timeMatch[8] || '0', 10)
      );
      
      entries.push({
        index,
        startTime,
        endTime,
        text: text.trim(),
      });
    }
  }
  
  return entries;
}

/**
 * Extract text from VTT (WebVTT) subtitle file
 * Format:
 * WEBVTT
 * 
 * 00:00:05.000 --> 00:00:10.000
 * [Speaker 1] This is the first subtitle.
 */
async function extractFromVtt(file: File): Promise<string> {
  const content = await file.text();
  const entries = parseVttFile(content);
  
  // Convert to transcript format with timestamps
  return entries
    .map(entry => {
      const timestamp = formatTimestamp(entry.startTime);
      return `[${timestamp}] ${entry.text}`;
    })
    .join('\n');
}

/**
 * Parse VTT file content into subtitle entries
 */
function parseVttFile(content: string): SubtitleEntry[] {
  const entries: SubtitleEntry[] = [];
  
  // Remove WEBVTT header
  const withoutHeader = content.replace(/^WEBVTT[^\n]*\n+/, '');
  const blocks = withoutHeader.trim().split(/\n\s*\n/);
  
  let index = 1;
  
  for (const block of blocks) {
    const lines = block.split('\n');
    
    if (lines.length < 2) continue;
    
    // VTT can have optional cue identifiers, so find the line with timing
    let timeLineIdx = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line && line.includes('-->')) {
        timeLineIdx = i;
        break;
      }
    }
    
    const timeLine = lines[timeLineIdx];
    if (!timeLine) continue; // Skip if no time line found
    
    const text = lines.slice(timeLineIdx + 1).join(' ');
    
    // Parse time: 00:00:05.000 --> 00:00:10.000
    const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
    
    if (timeMatch && timeMatch.length >= 9) {
      const startTime = parseTimeToSeconds(
        parseInt(timeMatch[1] || '0', 10),
        parseInt(timeMatch[2] || '0', 10),
        parseInt(timeMatch[3] || '0', 10),
        parseInt(timeMatch[4] || '0', 10)
      );
      
      const endTime = parseTimeToSeconds(
        parseInt(timeMatch[5] || '0', 10),
        parseInt(timeMatch[6] || '0', 10),
        parseInt(timeMatch[7] || '0', 10),
        parseInt(timeMatch[8] || '0', 10)
      );
      
      entries.push({
        index,
        startTime,
        endTime,
        text: text.trim(),
      });
      
      index++;
    }
  }
  
  return entries;
}

/**
 * Convert time components to seconds
 */
function parseTimeToSeconds(hours: number, minutes: number, seconds: number, milliseconds: number): number {
  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
}

/**
 * Format seconds as HH:MM:SS timestamp
 */
function formatTimestamp(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Check if file type is supported
 * Currently: TXT, SRT, VTT (DOCX and PDF require additional libraries)
 */
export function isSupportedFileType(fileName: string): boolean {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return ['txt', 'srt', 'vtt'].includes(extension || '');
}

/**
 * Get supported file extensions for file input
 * Currently: TXT, SRT, VTT
 * Future: DOCX, PDF (after installing mammoth and pdfjs-dist)
 */
export function getSupportedExtensions(): string {
  return '.txt,.srt,.vtt';
}

